import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import NetworkMap from '../components/NetworkMap';
import Loader from '../components/Loader';

/* Admins only: is the network working? */

function Stat({ value, label }) {
  return <div className="ad-stat"><b>{value ?? '—'}</b><span>{label}</span></div>;
}

function pct(n, d) {
  return d ? `${Math.round((n / d) * 100)}%` : '—';
}

const REASON_LABEL = {
  not_relevant: 'not relevant', wrong_stage: 'wrong stage', know_them: 'already know them',
  busy: 'busy', 'no reason': 'no reason',
};

/* Accounts for app-store reviewers (Apple's TestFlight review): whatever they
   post is visible to admins only, so testing never shows up for members. */
function ReviewAccounts({ notify }) {
  const [rows, setRows] = useState([]);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.reviewAccounts().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  async function set(addr, on) {
    setBusy(true);
    try { await api.setReviewAccount(addr, on); notify(on ? 'Marked as a review account' : 'Back to a normal account'); setEmail(''); load(); }
    catch (e) { notify(e.message); } finally { setBusy(false); }
  }
  return (
    <section className="card pad">
      <h2 className="ad-h">Review accounts</h2>
      <p className="muted-sm">For app-store reviewers. Their posts, comments and messages are visible to admins only, and they don't appear in Discover.</p>
      {rows.map((r) => (
        <div key={r.id} className="ad-row">
          <span><b>{r.name}</b> <span className="muted-sm">{r.email}</span></span>
          <button className="btn quiet sm" disabled={busy} onClick={() => set(r.email, false)}>Unmark</button>
        </div>
      ))}
      <form className="ad-row" onSubmit={(e) => { e.preventDefault(); if (email.trim()) set(email.trim(), true); }}>
        <input className="in" type="email" placeholder="Reviewer's email" value={email} onChange={(e) => setEmail(e.target.value)} aria-label="Reviewer's email" />
        <button className="btn primary sm" disabled={busy || !email.includes('@')}>Mark as review account</button>
      </form>
    </section>
  );
}

function Payments({ notify }) {
  const [p, setP] = useState(null);
  const [g, setG] = useState({ email: '', gb: '1', months: '3' });
  const load = useCallback(() => api.adminPayments().then(setP).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!p) return null;
  const grant = async () => {
    try {
      const r = await api.adminGrantStorage({ email: g.email.trim(), gb: Number(g.gb), months: Number(g.months), note: 'Granted by an admin' });
      notify?.(`Granted ${g.gb} GB to ${r.member}`); setG({ ...g, email: '' }); load();
    } catch (e) { notify?.(e.message); }
  };
  return (
    <section className="card pad">
      <h2 className="ad-h">Payments</h2>
      <div className="ad-grid">
        <Stat value={`$${p.revenue_usd.toFixed(2)}`} label="received (USDC)" />
        <Stat value={p.paid_count} label="payments" />
        <Stat value={p.credit_pending} label="top-ups waiting for Aegis" />
      </div>
      <p className="muted-sm">Wallet <code>{p.address}</code> on {p.networks.join(', ')}</p>
      {p.orders.filter((o) => o.status === 'paid').slice(0, 10).map((o) => (
        <p key={o.id} className="muted-sm">{new Date(o.paid_at).toLocaleDateString()} · {o.member?.name} · {o.label} · {o.paid_usdc} USDC on {o.network} · <code>{o.tx_hash.slice(0, 12)}…</code></p>
      ))}
      <div className="invite-row">
        <input className="in" placeholder="member email" value={g.email} onChange={(e) => setG({ ...g, email: e.target.value })} />
        <input className="in sm" type="number" min="0.1" max="100" step="0.5" value={g.gb} onChange={(e) => setG({ ...g, gb: e.target.value })} /> GB
        <input className="in sm" type="number" min="1" max="24" value={g.months} onChange={(e) => setG({ ...g, months: e.target.value })} /> months
        <button className="btn ghost sm" disabled={!g.email.trim()} onClick={grant}>Grant storage</button>
      </div>
    </section>
  );
}

function ReferralReview({ notify }) {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.pendingReferralPosts().then(setRows).catch(() => setRows([])), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return null;
  const act = async (id, approve) => {
    const reason = approve ? '' : window.prompt('Why not? (sent to them)');
    if (!approve && reason === null) return;
    try { await api.reviewReferralPost(id, approve, reason || ''); notify?.(approve ? 'Approved — it’s live' : 'Not approved'); load(); }
    catch (e) { notify?.(e.message); }
  };
  return (
    <section className="card pad">
      <h2 className="ad-h">Referral rewards {rows.length > 0 && <em className="ad-badge">{rows.length} to review</em>}</h2>
      {rows.length === 0 && <p className="muted-sm">Nothing waiting. Referral posts with a cash reward appear here before they go live.</p>}
      {rows.map((p) => (
        <div key={p.id} className="ad-report pending">
          <div><b>{p.title}</b> · {p.fields.company} · <b>{p.fields.reward_currency} {Number(p.fields.reward_amount).toLocaleString()}</b></div>
          <div className="muted-sm">by {p.author?.name} · {p.fields.reward_terms}{p.fields.link ? <> · <a href={p.fields.link} target="_blank" rel="noreferrer noopener">job link</a></> : null}</div>
          {p.body && <p className="ad-note">“{p.body}”</p>}
          <div className="mr-actions">
            <button className="btn primary sm" onClick={() => act(p.id, true)}>Approve</button>
            <button className="btn quiet sm" onClick={() => act(p.id, false)}>Don't approve</button>
          </div>
        </div>
      ))}
    </section>
  );
}

function AdsReview({ notify }) {
  const [ads, setAds] = useState(null);
  const load = useCallback(() => api.adminAds().then(setAds).catch((e) => notify?.(e.message)), [notify]);
  useEffect(() => { load(); }, [load]);
  if (!ads) return null;
  const act = async (id, decision, ask) => {
    const note = ask ? window.prompt(ask) : '';
    if (ask && note === null) return;
    try { await api.adminReviewAd(id, decision, note || ''); notify?.('Done'); load(); } catch (e) { notify?.(e.message); }
  };
  const waiting = ads.filter((a) => a.status === 'pending' || (a.status === 'paused' && a.paused_reason.startsWith('Paused automatically')));
  return (
    <section className="card pad">
      <h2 className="ad-h">Ads {waiting.length > 0 && <em className="ad-badge">{waiting.length} to review</em>}</h2>
      {ads.length === 0 && <p className="muted-sm">No ads yet.</p>}
      {ads.map((a) => (
        <div key={a.id} className={`ad-report ${a.status}`}>
          <div><b>{a.name}</b> <span className={`ad-status ${a.status}`}>{a.status}</span>
            {a.safety !== 'ok' && <span className="mr-tag">link: {a.safety}</span>}</div>
          <div className="muted-sm">by {a.owner?.name} ({a.owner?.email}) · <a href={a.url} target="_blank" rel="noreferrer noopener">{a.domain}</a></div>
          <p className="ad-note">“{a.pitch}” {a.perk ? `· 🎁 ${a.perk}` : ''}</p>
          {a.paused_reason && <p className="muted-sm">{a.paused_reason}</p>}
          <p className="muted-sm">{a.funnel.views} seen · {a.funnel.clicks} visits · {a.funnel.perk_claims} perks · {a.funnel.connected} connected
            {Object.values(a.outcomes).length ? ` · outcomes: ${Object.values(a.outcomes).map((o) => `${o.label} ${o.count}`).join(', ')}` : ''}</p>
          <div className="mr-actions">
            {['pending', 'paused', 'rejected'].includes(a.status) && <button className="btn primary sm" onClick={() => act(a.id, 'approve')}>Approve</button>}
            {a.status === 'pending' && <button className="btn ghost sm" onClick={() => act(a.id, 'reject', 'What should they change? (sent to them)')}>Ask for changes</button>}
            {!['removed', 'ended'].includes(a.status) && <button className="btn quiet sm" onClick={() => act(a.id, 'remove', 'Why is it being taken down? (sent to them)')}>Take down</button>}
          </div>
        </div>
      ))}
    </section>
  );
}

/* "Request an invite" from the landing page. Approve → a real invite is made
   for their email and sent; decline → nothing is sent. */
function InviteRequests({ notify }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState('');
  const [showDone, setShowDone] = useState(false);
  const load = useCallback(() => { api.adminInviteRequests().then(setRows).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);
  async function decide(r, d) {
    if (d === 'approve' && !window.confirm(`Send ${r.name} an invite at ${r.email}?`)) return;
    setBusy(r.id);
    try { await api.decideInviteRequest(r.id, d); notify(d === 'approve' ? `Invite sent to ${r.email}` : 'Declined — nothing was sent'); load(); }
    catch (e) { notify(e.message); } finally { setBusy(''); }
  }
  const waiting = rows.filter((r) => r.status === 'pending');
  const done = rows.filter((r) => r.status !== 'pending');
  return (
    <section className="card pad">
      <h2 className="ad-h">Invite requests {waiting.length > 0 && <em className="ad-badge">{waiting.length} waiting</em>}</h2>
      <p className="muted-sm">From “Request an invite” on the public page. Approving creates an invite for their email and sends it.</p>
      {waiting.length === 0 && <p className="muted-sm">Nothing waiting.</p>}
      {waiting.map((r) => (
        <div key={r.id} className="ad-item">
          <div><b>{r.name}</b> <span className="muted-sm">{r.email} · {new Date(r.created_at).toLocaleDateString()}</span></div>
          {r.link && <div className="muted-sm"><a href={/^https?:\/\//.test(r.link) ? r.link : `https://${r.link}`} target="_blank" rel="noreferrer">{r.link}</a></div>}
          {r.building && <p className="ad-note">“{r.building}”</p>}
          <div className="mr-actions">
            <button className="btn primary sm" disabled={busy === r.id} onClick={() => decide(r, 'approve')}>Approve &amp; send invite</button>
            <button className="btn ghost sm" disabled={busy === r.id} onClick={() => decide(r, 'decline')}>Decline</button>
          </div>
        </div>
      ))}
      {done.length > 0 && (
        <button className="linklike" onClick={() => setShowDone(!showDone)}>{showDone ? 'Hide' : 'Show'} {done.length} reviewed</button>
      )}
      {showDone && done.map((r) => (
        <div key={r.id} className="ad-row"><span><b>{r.name}</b> <span className="muted-sm">{r.email}</span></span>
          <span className={`ad-status ${r.status}`}>{r.status}{r.invite_code ? ` · ${r.invite_code}` : ''}</span></div>
      ))}
    </section>
  );
}

export default function AdminDashboard({ notify, go }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');
  const [tab, setTab] = useState('dash');

  const load = useCallback(() => {
    api.adminDashboard().then(setD).catch((e) => notify?.(e.message));
  }, [notify]);
  useEffect(load, [load]);

  if (!d) return <div className="page"><Loader label="Loading dashboard…" /></div>;

  async function act(key, fn, ok, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(key);
    try {
      await fn();
      notify?.(ok);
      load();
    } catch (e) {
      notify?.(e.message);
    } finally {
      setBusy('');
    }
  }

  const f = d.funnel;
  const open = d.reports.filter((r) => r.status === 'open');

  return (
    <div className="page admin-dash">
      <div className="admin-head">
        <h1 className="sec-title">Admin</h1>
        <div className="admin-tabs">
          <button className={`feed-view${tab === 'dash' ? ' on' : ''}`} aria-current={tab === 'dash' ? 'page' : undefined} onClick={() => setTab('dash')}>Dashboard</button>
          <button className={`feed-view${tab === 'network' ? ' on' : ''}`} aria-current={tab === 'network' ? 'page' : undefined} onClick={() => setTab('network')}>
            Network map <em className="beta-tag">Beta</em>
          </button>
          <button className="feed-view" onClick={() => go?.('matching')}>Matching review</button>
        </div>
      </div>

      {tab === 'network' ? <NetworkMap notify={notify} admin /> : (<>

      <section className="card pad">
        <h2 className="ad-h">Members</h2>
        <div className="ad-grid">
          <Stat value={d.members.total} label="members" />
          <Stat value={d.members.active_this_week} label="active this week" />
          <Stat value={d.members.joined_this_week} label="joined this week" />
          <Stat value={d.members.ready_to_match} label="ready to be matched" />
          <Stat value={d.members.match_profile_confirmed} label="match profile confirmed" />
          <Stat value={d.members.on_current_terms} label="accepted the terms" />
        </div>
      </section>

      <InviteRequests notify={notify} />
      <ReviewAccounts notify={notify} />

      <section className="card pad">
        <h2 className="ad-h">Introductions — last 30 days</h2>
        <div className="ad-funnel">
          {[['Suggested', f.suggested], ['One said yes', f.one_accepted], ['Connected', f.connected],
            ['Messaged', f.messaged], ['Replied', f.replied]].map(([label, n], i, all) => (
              <div key={label} className="ad-step">
                <b>{n}</b><span>{label}</span>
                {i > 0 && <em>{pct(n, all[i - 1][1])} of previous</em>}
              </div>
          ))}
        </div>
        {Object.keys(f.declined_because).length > 0 && (
          <p className="muted-sm">Declined because: {Object.entries(f.declined_because)
            .map(([k, n]) => `${REASON_LABEL[k] || k} ${n}`).join(' · ')}</p>
        )}
        {Object.keys(f.by_engine).length > 0 && (
          <p className="muted-sm">Made by: {Object.entries(f.by_engine).map(([k, n]) => `${k} ${n}`).join(' · ')}</p>
        )}
      </section>

      <section className="card pad">
        <h2 className="ad-h">This week</h2>
        <div className="ad-grid">
          <Stat value={d.engagement.messages} label="messages" />
          <Stat value={d.engagement.copilot_questions} label="Copilot questions" />
          <Stat value={d.engagement.copilot_find_requests} label="'find me someone' asks" />
          <Stat value={d.engagement.posts} label="posts" />
          <Stat value={d.engagement.files} label="files uploaded" />
          <Stat value={d.engagement.profile_views} label="profile views" />
        </div>
      </section>

      <section className="card pad">
        <h2 className="ad-h">Invites, email, AI</h2>
        <div className="ad-grid">
          <Stat value={`${d.invites.used} / ${d.invites.created}`} label="invites used / created" />
          <Stat value={d.invites.open} label="invites open" />
          <Stat value={d.invites.credit_owed} label="invite credit still owed" />
          <Stat value={d.email.sent || 0} label="emails sent this week" />
          <Stat value={d.email.failed || 0} label="emails failed" />
          <Stat value={`$${d.ai.copilot_spend_month_usd.toFixed(2)}`} label="Copilot spend, 30 days" />
          <Stat value={d.ai.judge_verdicts_week} label="matcher judge calls this week" />
        </div>
        {d.invites.credit_owed > 0 && (
          <p className="muted-sm">Invite credit lands once the Aegis credit key is set up.</p>
        )}
      </section>

      <section className="card pad">
        <h2 className="ad-h">Matcher</h2>
        <p>
          Mode <b>{d.matcher.mode}</b> · {d.matcher.labelled_pairs} pairs labelled · rules agree{' '}
          {d.matcher.rules_agreement == null ? '—' : `${Math.round(d.matcher.rules_agreement * 100)}%`} · judge agrees{' '}
          {d.matcher.judge_agreement == null ? '—' : `${Math.round(d.matcher.judge_agreement * 100)}%`}{' '}
          <button className="linklike" onClick={() => go('matching')}>Open matching review →</button>
        </p>
      </section>

      <Payments notify={notify} />

      <ReferralReview notify={notify} />
      <AdsReview notify={notify} />

      <section className="card pad">
        <h2 className="ad-h">Reports {open.length > 0 && <em className="ad-badge">{open.length} open</em>}</h2>
        {d.reports.length === 0 && <p className="muted-sm">No reports. Good.</p>}
        {d.reports.map((r) => (
          <div key={r.id} className={`ad-report ${r.status}`}>
            <div>
              <b>{r.reported?.name}</b> <span className="muted-sm">{r.reported?.email}</span>
              {r.reported && !r.reported.active && <span className="mr-tag">closed</span>}
              {r.times_reported > 1 && <span className="mr-tag">reported {r.times_reported}×</span>}
            </div>
            <div className="muted-sm">
              {r.reason} — by {r.reporter?.name} · {new Date(r.at).toLocaleDateString()} · {r.status}
            </div>
            {r.note && <p className="ad-note">“{r.note}”</p>}
            {r.status === 'open' && (
              <div className="mr-actions">
                <button className="btn ghost sm" disabled={busy === r.id}
                  onClick={() => act(r.id, () => api.dismissReport(r.id), 'Report dismissed')}>Dismiss</button>
                <button className="btn ghost sm" disabled={busy === r.id}
                  onClick={() => act(r.id, () => api.closeReportedAccount(r.id), 'Account closed',
                    `Close ${r.reported?.name}'s account? Nothing is deleted and you can reopen it.`)}>
                  Close their account
                </button>
              </div>
            )}
            {r.reported && !r.reported.active && (
              <button className="linklike" disabled={busy === `re-${r.id}`}
                onClick={() => act(`re-${r.id}`, () => api.reactivateMember(r.reported.id), 'Account reopened')}>
                Reopen account
              </button>
            )}
          </div>
        ))}
      </section>
      <p className="muted-sm">As of {new Date(d.as_of).toLocaleString()} · <button className="linklike" onClick={load}>refresh</button></p>
      </>)}
    </div>
  );
}
